import { INTERACTION_TYPE_ICONS, type InteractionType } from "@/api/types/interaction";

/**
 * Icon for a merged timeline row. Logged-interaction kinds get their real
 * icon; lifecycle kinds (inquiry/quote/event/invoice/payment/review/
 * outreach/tasting/...) get a small curated set, and anything the backend
 * adds later falls back to a neutral dot rather than failing to render.
 */
const LIFECYCLE_ICONS: Record<string, string> = {
  inquiry: "📥",
  quote: "📄",
  event: "🎉",
  invoice: "🧾",
  payment: "💳",
  review: "⭐",
  outreach: "📣",
  tasting: "🥂",
};

export function timelineIcon(kind: string): string {
  if (kind in INTERACTION_TYPE_ICONS) {
    return INTERACTION_TYPE_ICONS[kind as InteractionType];
  }
  return LIFECYCLE_ICONS[kind] ?? "•";
}

/** "Just now" / "5m ago" / "3h ago" / "4d ago" / short date beyond a week. */
export function relativeTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);

  if (diffSec < 60) return "Just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  const diffDay = Math.round(diffHour / 24);
  if (diffDay < 7) return `${diffDay}d ago`;

  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDateOnly(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
